import { ConfigurableColumns } from '@/components/ConfigurableColumns';
import { CopiableIdDataGridField } from '@/components/CopiableIdField';
import { GenericDateFunctionField } from '@/components/GenericDateFunctionField';
import { Chip, Tooltip, type ChipProps } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MicIcon from '@mui/icons-material/Mic';
import { DateField, FunctionField } from 'react-admin';

import { isManualSessionNoteProcessingFrom } from './processingFromSemantics';

const payStatusChipColor = (status: string): ChipProps['color'] => {
  switch (status) {
    case 'customer':
    case 'reactivated':
    case 'free-subscription':
      return 'success';
    case 'trialing':
      return 'info';
    case 'lifelong-trial':
      return 'secondary';
    case 'trial-completed':
    case 'pending-cancellation':
      return 'warning';
    case 'billing-issues':
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

/**
 * On-device transcription provenance (#146). Every aggregated note gets
 * `transcriptSource: 'server'`, so only notes that were device-transcribed or
 * pinned to shadow/primary (or reported a device envelope) get a label.
 */
const transcriptionLabel = (record: any): string | null => {
  const mode = record.env?.onDeviceTranscription;
  const device = record.deviceTranscription;
  const gate = record.deviceTranscriptGate;

  if (record.transcriptSource === 'device') {
    return 'Device';
  }
  if (mode !== 'shadow' && mode !== 'primary' && !device) {
    return null;
  }
  if (device?.intent === 'server') {
    return `Server (${device.reason ?? 'ineligible'})`;
  }
  if (gate?.status?.startsWith('fallback_')) {
    return `Server (fallback: ${gate.reason ?? 'unknown'})`;
  }
  // The gate's timeout cron is not guaranteed to run, so an undecided primary
  // note must not read as served by the server. A published transcript (an older
  // app's primary-pinned note goes straight to the server) is never pending.
  if (
    mode === 'primary' &&
    !record.transcriptSource &&
    (!gate?.status || gate.status === 'waiting')
  ) {
    return 'Pending';
  }
  if (mode === 'shadow' && device?.state === 'failed') {
    return `Server (shadow, device failed: ${
      device.reason ?? 'unknown'
    })`;
  }
  if (mode === 'shadow') {
    return 'Server (shadow)';
  }
  return 'Server';
};

const transcriptionDetail = (record: any) =>
  [
    record.env?.onDeviceTranscription &&
      `mode ${record.env.onDeviceTranscription}`,
    record.deviceTranscription &&
      `device ${record.deviceTranscription.intent} ${record.deviceTranscription.state}`,
    record.deviceTranscription?.engine,
    record.deviceTranscription?.diarizer,
    record.deviceTranscriptGate?.status &&
      `gate ${record.deviceTranscriptGate.status}`,
  ]
    .filter(Boolean)
    .join(' · ');

const toFixed3 = (value: unknown) =>
  typeof value === 'number' ? value.toFixed(3) : '';

const msToSeconds = (value: unknown) =>
  typeof value === 'number' ? (value / 1000).toFixed(1) : '';

/** Hidden column whose grid cell and CSV value share one formatter. */
const formattedColumn = (
  label: string,
  source: string,
  format: (record: any) => string,
): ConfigurableColumns => ({
  label,
  source,
  sortable: false,
  GridComponent: FunctionField,
  gridComponentProps: { render: format },
  exportFn: format,
  omit: true,
});

export const columns = (
  hideDeleted: boolean,
): ConfigurableColumns[] => [
  // Default visible columns in the requested order
  {
    label: 'Note Date',
    source: 'createdAt',
    GridComponent: DateField,
    sortable: true,
    exportFn: (doc: any) =>
      doc.createdAt ? new Date(doc.createdAt).toISOString() : '',
  },
  {
    label: 'Note Time',
    source: 'createdAtTime',
    key: 'createdAtTime',
    sortBy: 'createdAt',
    sortable: true,
    GridComponent: GenericDateFunctionField,
    gridComponentProps: { format: 'HH:mm:ss', valueSource: 'createdAt' },
    exportFn: false,
  },
  { label: 'App Version', source: 'appVersion' },
  {
    label: 'First Name',
    sortable: false,
    source: 'therapistFirstName',
  },
  {
    label: 'Last Name',
    sortable: false,
    source: 'therapistLastName',
  },
  { label: 'Email', sortable: false, source: 'therapistEmail' },
  {
    label: 'Therapist Id',
    source: 'therapistId',
    exportFn: record => record.therapistId,
    sortable: false,
    GridComponent: CopiableIdDataGridField,
    gridComponentProps: {
      targetNavigationResource: 'therapists',
      referenceField: '_id',
      filter: ['therapistId', 'therapistId'],
      targetNavigationFilters: {
        isHiddenTestAccount: 'visible',
      },
    },
  },
  {
    label: 'Note Type',
    source: 'live',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) => (
        <Chip
          label={record.live ? 'Live' : 'Dictation'}
          color={record.live ? 'primary' : 'secondary'}
        />
      ),
    },
  },
  {
    label: 'Sample Note',
    source: 'isSampleNote',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) => (
        <Chip
          label={record.isSampleNote ? 'Yes' : 'No'}
          color={record.isSampleNote ? 'success' : 'default'}
        />
      ),
    },
  },
  {
    label: 'Narrative Word Count',
    source: 'wordCount.narrative',
  },
  { label: 'Framework', source: 'subType' },
  {
    label: 'Pay Status',
    source: 'payStatus',
    sortable: false,
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) => {
        const raw = record.payStatus;
        if (raw == null || raw === '') {
          return null;
        }
        const label = String(raw);
        return (
          <Chip
            label={label}
            color={payStatusChipColor(label)}
            size="small"
          />
        );
      },
    },
    exportFn: (record: any) => record.payStatus ?? '',
  },
  { label: 'Status', source: 'status' },
  {
    label: 'Transcription',
    source: 'transcriptSource',
    sortable: false,
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) => {
        const label = transcriptionLabel(record);
        if (!label) {
          return null;
        }
        return (
          <Tooltip title={transcriptionDetail(record)}>
            <Chip
              label={label}
              color={label === 'Device' ? 'success' : 'default'}
              size="small"
            />
          </Tooltip>
        );
      },
    },
    exportFn: (record: any) => transcriptionLabel(record) ?? '',
  },
  // Hidden columns (omit: true)
  {
    label: 'Note Id',
    omit: true,
    source: 'id',
    sortable: true,
    exportFn: (doc: any) => doc.id,
  },
  {
    label: 'Timezone',
    source: 'deviceInfo.timezone.offset',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) =>
        record.deviceInfo?.timezone ? (
          <Chip
            label={`${record.deviceInfo.timezone.name} (${record.deviceInfo.timezone.gmt})`}
          />
        ) : null,
    },
    omit: true,
  },
  {
    label: 'Title',
    source: 'therapistTitle',
    sortable: false,
    omit: true,
  },
  {
    label: 'Client Id',
    sortable: false,
    source: 'patientId',
    GridComponent: CopiableIdDataGridField,
    omit: true,
  },
  {
    label: 'Processing Type',
    sortable: false,
    source: 'processingHistory.from',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) => {
        if (record.status !== 'processed') {
          return null;
        }

        const hasManualProcessing = record.processingHistory?.some(
          (ph: { from?: string | null }) =>
            isManualSessionNoteProcessingFrom(ph.from),
        );

        return (
          <Chip
            label={hasManualProcessing ? 'Manual' : 'Automatic'}
            color={hasManualProcessing ? 'warning' : 'info'}
          />
        );
      },
    },
    exportFn: (record: any) => {
      if (record.status !== 'processed') {
        return null;
      }

      return record.processingHistory?.some((ph: { from?: string | null }) =>
        isManualSessionNoteProcessingFrom(ph.from),
      )
        ? 'Manual'
        : 'Automatic';
    },
    omit: true,
  },
  { label: 'Files Count', sortable: false, source: 'filesCount', omit: true },
  {
    label: 'Media Type',
    source: 'mediaType',
    GridComponent: FunctionField,
    sortable: false,
    gridComponentProps: {
      render: (record: any) => (
        <Chip
          icon={
            record.mediaType === 'text' ? (
              <EditIcon />
            ) : record.mediaType === 'audio' ? (
              <MicIcon />
            ) : undefined
          }
          color={
            record.mediaType === 'text'
              ? 'success'
              : record.mediaType === 'audio'
                ? 'info'
                : 'default'
          }
          size="small"
          sx={{
            color: 'white',
            padding: '12px',
            backgroundColor:
              record.mediaType === 'text' ? '#4caf50' : undefined,
            '& .MuiChip-icon': {
              color: 'white',
              margin: 0,
              fontSize: '1.2rem',
            },
            '& .MuiChip-label': {
              display: 'none',
            },
          }}
        />
      ),
    },
    omit: true,
  },
  { label: 'App Fingerprint', source: 'appFingerprint', omit: true },
  {
    label: 'Deleted At',
    source: 'deletedAt',
    omit: true,
    GridComponent: GenericDateFunctionField,
  },
  formattedColumn(
    'On-device Mode',
    'env.onDeviceTranscription',
    record => record.env?.onDeviceTranscription ?? '',
  ),
  formattedColumn(
    'Device WER',
    'deviceTranscriptGate.comparison.wer',
    record => {
      const comparison = record.deviceTranscriptGate?.comparison;
      return toFixed3(comparison?.wer) || (comparison?.error ?? '');
    },
  ),
  formattedColumn(
    'Speaker Agreement',
    'deviceTranscriptGate.comparison.speakerAgreement',
    record =>
      toFixed3(
        record.deviceTranscriptGate?.comparison?.speakerAgreement,
      ),
  ),
  formattedColumn(
    'Speakers (device/server)',
    'deviceTranscriptGate.comparison.deviceSpeakers',
    record => {
      const comparison = record.deviceTranscriptGate?.comparison;
      if (comparison?.deviceSpeakers == null) {
        return '';
      }
      return `${comparison.deviceSpeakers}/${
        comparison.serverSpeakers ?? '?'
      }`;
    },
  ),
  formattedColumn(
    'Coverage',
    'deviceTranscriptGate.comparison.coverage',
    record =>
      toFixed3(record.deviceTranscriptGate?.comparison?.coverage),
  ),
  formattedColumn(
    'Would Accept',
    'deviceTranscriptGate.comparison.wouldAccept',
    record => {
      const comparison = record.deviceTranscriptGate?.comparison;
      if (comparison?.wouldAccept == null) {
        return '';
      }
      if (comparison.wouldAccept) {
        return 'Yes';
      }
      return comparison.rejectReason
        ? `No (${comparison.rejectReason})`
        : 'No';
    },
  ),
  formattedColumn(
    'Device Ready (s)',
    'deviceTranscription.metrics.stopToReadyMs',
    record =>
      msToSeconds(record.deviceTranscription?.metrics?.stopToReadyMs),
  ),
  formattedColumn(
    'Diarization (s)',
    'deviceTranscription.metrics.diarizationMs',
    record =>
      msToSeconds(record.deviceTranscription?.metrics?.diarizationMs),
  ),
  formattedColumn(
    'Device Reason',
    'deviceTranscription.reason',
    record => record.deviceTranscription?.reason ?? '',
  ),
];
